// order-picker-20261021 / drawer-remove-20261021: long-press BUY order picker
// plus the tiny armed-order status under BUY.
// Picker opens off the LEFT of BUY (flips right if no room). While the press
// is held WalletHoldings owns the pointer (captured on the button) and
// hit-tests rows/chips via data-oe-pick / data-oe-chip. In edit mode (release
// on a value chip) the picker is a normal interactive popup.
import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ORDERS } from './OrderEntryDrawer'

const W = 200
const GAP = 6

const STYLE = `
  .oe-picker, .oe-buy-status { position: fixed; z-index: 100000; box-sizing: border-box;
    background: rgba(60,255,140,0.10); border: 1px solid rgba(80,255,150,0.35); border-radius: 6px;
    box-shadow: 0 0 14px rgba(60,255,140,0.18), inset 0 0 10px rgba(60,255,140,0.06);
    backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
    font-size: 10px; color: #c8ffe0; user-select: none; }
  .oe-picker { padding: 4px; touch-action: none; transform-origin: right center; animation: oe-pick-in 120ms ease-out; }
  .oe-picker.flip { transform-origin: left center; }
  @keyframes oe-pick-in { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
  .oe-pick { cursor: pointer; padding: 4px 6px 4px 7px; border-radius: 4px; border: 1px solid transparent; }
  .oe-pick + .oe-pick { margin-top: 2px; }
  .oe-pick.hot { background: rgba(80,255,150,0.20); border-color: rgba(80,255,150,0.45); color: #eafff2; }
  .oe-pick-head { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
  .oe-pick-label { font-weight: 600; letter-spacing: 0.03em; display: flex; align-items: center; gap: 5px; }
  .oe-pick-dot { width: 5px; height: 5px; border-radius: 50%; background: #50ff96; box-shadow: 0 0 4px #50ff96; }
  .oe-chip { padding: 0 5px; border-radius: 3px; border: 1px solid rgba(80,255,150,0.25); color: rgba(220,255,235,0.85); font-size: 9px; line-height: 14px; white-space: nowrap; }
  .oe-chip.hot { background: rgba(80,255,150,0.28); border-color: rgba(80,255,150,0.6); color: #fff; }
  .oe-pick-desc { margin-top: 2px; font-size: 9px; line-height: 1.3; color: rgba(220,255,235,0.8); }
  .oe-edit { display: flex; align-items: center; gap: 4px; margin-top: 4px; }
  .oe-edit span { flex: 1; color: rgba(220,255,235,0.8); font-size: 9px; }
  .oe-edit input { width: 54px; background: rgba(0,0,0,0.35); color: #eafff2; border: 1px solid rgba(80,255,150,0.4); border-radius: 3px; padding: 1px 4px; font-size: 10px; }
  .oe-edit button { background: rgba(80,255,150,0.12); color: #c8ffe0; border: 1px solid rgba(80,255,150,0.35); border-radius: 3px; width: 18px; height: 16px; padding: 0; font-size: 11px; line-height: 14px; cursor: pointer; }
  .oe-g { font-size: 8px; font-weight: 700; color: #0b1a10; background: #50ff96; border-radius: 2px; padding: 0 2px; line-height: 10px; }
  .oe-new .oe-pick-label { font-weight: 500; color: rgba(220,255,235,0.75); }
  .oe-edit textarea.oe-ask { width: 100%; flex: 1; box-sizing: border-box; resize: none; overflow: hidden; background: rgba(0,0,0,0.35); color: #eafff2; border: 1px solid rgba(80,255,150,0.25); border-radius: 3px; padding: 2px 4px; font: inherit; font-size: 10px; line-height: 1.35; outline: none; box-shadow: none; }
  .oe-edit button.oe-del { width: 22px; color: #ffd0d0; border-color: rgba(255,120,120,0.45); background: rgba(255,80,80,0.12); }
  .oe-chain-n { min-width: 11px; height: 11px; border-radius: 6px; font-size: 8px; font-weight: 700; line-height: 11px; text-align: center;
    color: #eafff2; background: rgba(80,255,150,0.28); border: 1px solid rgba(80,255,150,0.6); box-shadow: 0 0 4px rgba(80,255,150,0.35); }
  .oe-buy-status { display: flex; align-items: center; gap: 5px; padding: 1px 4px 1px 6px; font-size: 9px; line-height: 14px; white-space: nowrap; overflow: hidden; }
  .oe-buy-status .txt { overflow: hidden; text-overflow: ellipsis; flex: 1; }
  .oe-buy-status .x { cursor: pointer; color: #eafff2; opacity: 0.8; padding: 0 2px; }
  .oe-buy-status .x:hover { opacity: 1; }
`

const clampTo = (s, v) => {
  let n = Number(v)
  if (!Number.isFinite(n)) return null
  if (s.min != null) n = Math.max(s.min, n)
  if (s.max != null) n = Math.min(s.max, n)
  return Math.round(n * 1000) / 1000
}

export default function OrderPicker({ rect, hover, chipHover, selected, edit, cfg, update, orders = ORDERS, onDelete, onAsk, onPick, chain = [], onChain }) {
  // order-chain-20261021: Ctrl/Cmd-click or touch long-press (~450ms) a row toggles it in the chain.
  const lp = useRef({ t: null, fired: false })
  const [mHover, setMHover] = useState(null)   // picker-hover-20261021: mouse hover shows desc while menu is open
  if (!rect) return null
  const lpStart = (id, e) => {
    if (e.pointerType !== 'touch' || id === '__new') return
    clearTimeout(lp.current.t); lp.current.fired = false
    lp.current.t = setTimeout(() => { lp.current.t = null; lp.current.fired = true; onChain?.(id); try { navigator.vibrate?.(12) } catch {} }, 450)
  }
  const lpEnd = () => { clearTimeout(lp.current.t); lp.current.t = null }
  const apply = (patch) => { if (patch) update?.(patch) }   // recipe settings save themselves (set returns null)
  const vw = window.innerWidth, vh = window.innerHeight
  let left = rect.left - GAP - W
  let flip = false
  if (left < GAP) { left = rect.right + GAP; flip = true }
  left = Math.max(GAP, Math.min(vw - W - GAP, left))
  const estH = (orders.length + 1) * 26 + 70
  const top = Math.max(GAP, Math.min(vh - estH - GAP, rect.top - 4))
  return createPortal(
    <div className={`oe-picker ${flip ? 'flip' : ''}`} style={{ left, top, width: W }}>
      {orders.map((o, i) => [o, i]).sort((a, b) => { const ca = chain.indexOf(a[0].id), cb = chain.indexOf(b[0].id); return (ca < 0 ? 99 : ca) - (cb < 0 ? 99 : cb) || a[1] - b[1] }).map(([o]) => {   /* chain-sort-20261021: chained rows first, in chain order */
        const s = o.settings?.[0]
        const v = s && cfg ? s.get(cfg) : null
        const editing = edit === o.id && (s || o.by === 'grok')
        return (
          <div key={o.id} data-oe-pick={o.id}
            onPointerDown={(e) => lpStart(o.id, e)} onPointerEnter={(e) => { if (e.pointerType !== 'touch') setMHover(o.id) }}
            onPointerUp={lpEnd} onPointerLeave={(e) => { lpEnd(); if (e.pointerType !== 'touch') setMHover(h => (h === o.id ? null : h)) }} onPointerCancel={lpEnd}
            onClick={(e) => {
              if (e.target.closest?.('.oe-edit')) return
              if (lp.current.fired) { lp.current.fired = false; return }
              if (e.ctrlKey || e.metaKey) { onChain?.(o.id); return }
              onPick?.(o.id)
            }}
            className={`oe-pick ${(hover === o.id && !chipHover) || editing ? 'hot' : ''} ${selected === o.id ? 'sel' : ''}`}>
            <div className="oe-pick-head">
              <div className="oe-pick-label">{chain.includes(o.id) && <span className="oe-chain-n">{chain.indexOf(o.id) + 1}</span>}{o.by === 'grok' && <span className="oe-g">G</span>}{o.label}{selected === o.id && <span className="oe-pick-dot" />}</div>
              {((s && v != null) || o.by === 'grok') && (
                <span data-oe-chip={o.id} onClick={(e) => { e.stopPropagation(); onPick?.(o.id, true) }} className={`oe-chip ${chipHover === o.id || editing ? 'hot' : ''}`}>
                  {s && v != null ? o.settings.map(st => { const x = st.get(cfg); return st.chip ? st.chip(x) : x }).join(' \u00b7 ') : '\u22ef'}
                </span>
              )}
            </div>
            {(hover === o.id || mHover === o.id) && !editing && o.desc && <div className="oe-pick-desc">{o.desc}</div>}
            {editing && s && o.settings.map((st, si) => { const sv = st.get(cfg); return (   /* push-target-20261022: one edit row per setting */
              <div className="oe-edit" key={si}>
                <span>{st.label}</span>
                <button onClick={() => { const n = clampTo(st, sv - (st.step || 1)); if (n != null) apply(st.set(n)) }}>-</button>
                <input type="number" inputMode="decimal" autoFocus={si === 0} step={st.step} min={st.min} max={st.max} value={sv}
                  onChange={(e) => { const n = clampTo(st, parseFloat(e.target.value)); if (n != null) apply(st.set(n)) }} />
                <button onClick={() => { const n = clampTo(st, sv + (st.step || 1)); if (n != null) apply(st.set(n)) }}>+</button>
              </div>
            ) })}
            {editing && o.by === 'grok' && (
              <div className="oe-edit">
                <span>{o.desc ? '' : 'Grok order'}</span>
                <button className="oe-del" title="Delete this order" onClick={() => onDelete?.(o.id)}>{'\u00d7'}</button>
              </div>
            )}
          </div>
        )
      })}
      <div data-oe-pick="__new" onClick={(e) => { if (!e.target.closest?.('.oe-edit')) onPick?.('__new', true) }} className={`oe-pick oe-new ${hover === '__new' && edit !== '__new' ? 'hot' : ''}`}>
        <div className="oe-pick-label">+ New order (ask Grok)</div>
        {edit === '__new' && (
          <div className="oe-edit">
            <textarea className="oe-ask" rows={2} autoFocus placeholder="Describe the order, Enter to send" ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px' } }} onInput={(e) => { const el = e.currentTarget; el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px' }}
              onKeyDown={(e) => { if (e.key !== 'Enter') return; if (e.shiftKey) { e.stopPropagation(); return } e.preventDefault(); if (e.currentTarget.value.trim()) onAsk?.(e.currentTarget.value.trim()) }} />
          </div>
        )}
      </div>
      <style>{STYLE}</style>
    </div>,
    document.body
  )
}

// Tiny one-line status under BUY while an order is armed/running.
export function OrderStatusLine({ rect, text, onCancel, anchor }) {
  // order-menu-anchor-20261022: anchor = BUY element -> render in-panel (absolute), moves/layers with the panel
  if (anchor && text) {
    const el = anchor
    return (
      <div className="oe-buy-status" style={{ position: 'absolute', zIndex: 5, left: el.offsetLeft, top: el.offsetTop + el.offsetHeight + 2, width: Math.max(120, el.offsetWidth) }}>
        <span className="txt" title={text}>{text}</span>
        <span className="x" title="Cancel" onClick={onCancel}>{'\u00d7'}</span>
        <style>{STYLE}</style>
      </div>
    )
  }
  if (!rect || !text) return null
  const w = Math.max(120, rect.right - rect.left)
  const left = Math.max(GAP, Math.min(window.innerWidth - w - GAP, rect.left))
  const top = Math.min(window.innerHeight - 20, rect.bottom + 2)
  return createPortal(
    <div className="oe-buy-status" style={{ left, top, width: w }}>
      <span className="txt" title={text}>{text}</span>
      <span className="x" title="Cancel" onClick={onCancel}>{'\u00d7'}</span>
      <style>{STYLE}</style>
    </div>,
    document.body
  )
}
